import type { SearchSource } from '@asyagrup/group-hub-contracts';

/**
 * "Son açtıklarınız" — the third client-side tier of the palette.
 *
 * Keyed PER USER, and that is the whole point of the design rather than a
 * detail of it. A shared office machine is the normal case here, and a single
 * `grouphub.search.recent` key would show the previous user's payee names and
 * personnel names to whoever logs in next — a leak with no server involved at
 * all, produced entirely by a convenience feature.
 *
 * And it lives in sessionStorage, not localStorage. Per-user keys stop the NEXT
 * person seeing these rows, but they did nothing about the same person's rows
 * outliving the session: `logout()` never runs when a tab is simply closed, so
 * on a shared workstation the last user's payee and personnel names sat on disk
 * until somebody else happened to log in and trigger the sweep. sessionStorage
 * dies with the tab by construction — no lifecycle hook to forget, nothing to
 * run on a path the browser may never take. The cost is that recents do not
 * survive a browser restart, which on a machine several people use is the
 * behaviour we want rather than a regression.
 *
 * Entries store only what was already on screen when the row was selected:
 * title, subtitle, the href, nothing else. No amount, no identifier the user
 * had not already been shown. And the target screen re-checks permission on
 * arrival, so a stale entry after a role change navigates to a screen that
 * refuses — never to data.
 */

export interface RecentEntry {
  source: SearchSource | 'screen' | 'action';
  id: string;
  title: string;
  subtitle: string | null;
  href: string;
  /** Epoch ms. Ordering only; never rendered. */
  at: number;
}

const PREFIX = 'grouphub.search.recent.';
const MAX = 8;

function keyFor(userId: string): string {
  return `${PREFIX}${userId}`;
}

/** sessionStorage everywhere below; see the note above for why not local. */
function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isEntry(value: unknown): value is RecentEntry {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.source === 'string' &&
    typeof row.id === 'string' &&
    typeof row.title === 'string' &&
    typeof row.href === 'string' &&
    typeof row.at === 'number'
  );
}

export function readRecents(userId: string | null): RecentEntry[] {
  if (!userId) return [];
  try {
    const raw = store()?.getItem(keyFor(userId)) ?? null;
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isEntry).slice(0, MAX);
  } catch {
    // A quota error, private-browsing mode, or a hand-edited value. A palette
    // that throws because its nice-to-have tier could not be read is worse
    // than one without the tier.
    return [];
  }
}

export function pushRecent(userId: string | null, entry: Omit<RecentEntry, 'at'>): void {
  if (!userId) return;
  try {
    const next = [
      { ...entry, at: Date.now() },
      ...readRecents(userId).filter((row) => !(row.source === entry.source && row.id === entry.id)),
    ].slice(0, MAX);
    store()?.setItem(keyFor(userId), JSON.stringify(next));
  } catch {
    /* see readRecents */
  }
}

/**
 * Drops every other account's recents from this browser.
 *
 * Called once when the palette first has a user id. The spec's version of this
 * clears the list inside `logout()`, which is stricter in one respect — it
 * removes the trail the moment the person leaves rather than when the next
 * person arrives — but it needs a hunk in `session-context.tsx`, and this
 * feature is deliberately confined to `features/search` plus one hunk in the
 * shell. The threat it exists for is the same and is closed either way: the
 * next login on a shared machine cannot see the previous user's names, because
 * the previous user's key is gone before the palette renders a single row.
 *
 * A logout-time clear is still the belt-and-braces addition when
 * `session-context.tsx` is in scope; it is not a substitute for this sweep,
 * since a session that ends by closing the tab never runs `logout()` at all.
 */
export function pruneForeignRecents(userId: string | null): void {
  if (!userId) return;
  try {
    // Two sweeps, and the localStorage one is the reason this still exists.
    //
    // Recents used to be written to localStorage, so machines that ran the
    // earlier build still carry those keys — including the ones belonging to
    // the person sitting there now. They are orphaned by the move to
    // sessionStorage and nothing else will ever delete them, so ALL of them go,
    // not merely other users'.
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(PREFIX)) doomed.push(key);
    }
    for (const key of doomed) window.localStorage.removeItem(key);

    // And within the session, another user logging into the same tab.
    const mine = keyFor(userId);
    const session = store();
    if (session) {
      const stale: string[] = [];
      for (let i = 0; i < session.length; i += 1) {
        const key = session.key(i);
        if (key && key.startsWith(PREFIX) && key !== mine) stale.push(key);
      }
      for (const key of stale) session.removeItem(key);
    }
  } catch {
    /* see readRecents */
  }
}
